import axios from 'axios';
import {
  GuestUpdateVisitDetailsDto,
  VisitGuestAttachmentControllerUploadVisitPrivate201Response,
} from 'woofwoof-api-public';
import { publicAxiosBaseUrl } from './client';
import { publicVisitApi } from './visit-public';

const base = publicAxiosBaseUrl;

export interface GuestVerifyStartResponse {
  challengeId: string;
  channel: 'email' | 'sms';
  maskedDestination: string;
  expiresAt: string;
}

export interface GuestVerifyCompleteResponse {
  id: string; // session id
  expiresAt: string;
}

// Visit detail shape — keep it permissive (the FE shapes are flexible enough).
// Mirrors GET /public/visit/:visitId. Concrete typing per-field happens in the
// forked subcomponents (Task 22) once we know exactly which fields each panel
// needs.
export interface GuestVisitDetail {
  id: string;
  startTime: string;
  endTime: string;
  category: string;
  visitType: string;
  status: string;
  description: string;
  rejectedBy: string | null;
  rejectionReason: string | null;
  visitAddress: unknown;
  userData: {
    firstName: string;
    lastName: string;
    email: string | null;
    phoneNumber: string | null;
  } | null;
  profileData: {
    name: string;
    breed?: string;
    petCategory?: string;
    weight?: number | null;
    gender?: string | null;
    dob?: string | null;
    chipNumber?: string | null;
  } | null;
  payment: {
    status: string;
    amount: number;
    paymentUrl: string | null;
  } | null;
  professional: {
    id: string;
    firstName: string;
    middleName: string | null;
    lastName: string;
    avatar: string | null;
    categories: string[];
  } | null;
  organization: {
    id: string;
    name: string;
    logoUrl: string | null;
    street: string | null;
    zip: string | null;
    city: string | null;
    phoneNumber: string | null;
    email: string | null;
    slug: string | null;
  } | null;
  service: {
    name: string;
    duration: number | null;
    price: number | null;
    description: string | null;
  } | null;
  reservationFeeAmount?: number | null;
  summary: string | null;
  notes: string | null;
  activity: GuestVisitActivityItem[];
  transcription: GuestVisitTranscription | null;
  medications: GuestVisitMedication[];
  treatments: GuestVisitTreatment[];
  reminders: GuestVisitReminder[];
  rating: GuestVisitRating | null;
  documents: GuestVisitDocument[];
  conversation: {
    roomName: string | null;
  } | null;
}

export interface GuestVisitRating {
  professionalRating: number;
  organizationRating: number;
  review: string;
}

// Post-visit clinical shapes. The BE returns whatever lives in the
// `medications` / `treatmentrecords` / `documents` collections keyed by
// visitId; we narrow only the fields the guest UI renders.
export interface GuestVisitActivityItem {
  id: string;
  type: 'MEDICATION' | 'TREATMENT' | 'REMINDER' | 'DOCUMENT' | string;
  action: 'ADDED' | 'UPDATED' | 'REMOVED' | string;
  referenceId: string;
  name: string;
  createdAt: string;
}

export interface GuestVisitTranscriptionSegment {
  id: string;
  speaker: 'professional' | 'caregiver' | 'unknown' | string;
  text: string;
  createdAt: string;
}

export interface GuestVisitTranscription {
  status: string | null;
  plainText: string;
  transcriptSummary: string | null;
  transcriptSummaryStatus: 'pending' | 'success' | 'failed' | string | null;
  segments: GuestVisitTranscriptionSegment[];
}

export interface GuestVisitMedication {
  _id?: string;
  id?: string;
  name: string;
  dosage?: string | null;
  dosageInterval?: string | null;
  notes?: string | null;
}

export interface GuestVisitTreatment {
  _id?: string;
  id?: string;
  name: string;
  treatmentDate?: string | null;
  priceBrutto?: number | null;
  associatedMedicines?: Array<{
    medicineProductId?: string;
    medicineName?: string;
  }>;
}

export interface GuestVisitDocument {
  _id?: string;
  id?: string;
  filename: string;
  url: string;
  originalFilename?: string | null;
  description?: string | null;
  uploadedAt?: string | null;
  createdAt?: string | null;
  // 'OWNER' for booking + Edytuj szczegóły uploads; 'STAFF' for clinic-side
  // in-visit attachments + AI summary PDF (professional or org admin).
  // Pre-existing rows default to 'OWNER' (see Document schema comment).
  uploadedByRole?: 'OWNER' | 'STAFF';
}

export interface GuestVisitReminder {
  _id?: string;
  id?: string;
  description: string;
  scheduledAt: string;
  recurrenceType: string;
  notificationChannels: string[];
  isActive?: boolean;
  visitId?: string;
}

export const guestVisitApi = {
  manageLinkStart: (
    handle: string,
  ): ReturnType<
    typeof publicVisitApi.visitGuestVerifyControllerStartFromManageLink
  > =>
    publicVisitApi.visitGuestVerifyControllerStartFromManageLink({
      guestManageLinkStartDto: { handle },
      userAgent: navigator.userAgent,
    }),

  verifyStart: (
    visitId: string,
    body: { guestAccessToken?: string; channel?: 'email' | 'sms' },
  ): ReturnType<typeof publicVisitApi.visitGuestVerifyControllerStart> =>
    publicVisitApi.visitGuestVerifyControllerStart({
      visitId,
      guestVerifyStartDto: body,
    }),

  verifyComplete: (
    visitId: string,
    body: { challengeId: string; code?: string; nonce?: string },
  ): ReturnType<typeof publicVisitApi.visitGuestVerifyControllerComplete> =>
    publicVisitApi.visitGuestVerifyControllerComplete({
      visitId,
      userAgent: navigator.userAgent,
      guestVerifyCompleteDto: body,
    }),

  getVisit: (visitId: string, sessionId: string) =>
    axios.get<GuestVisitDetail>(`${base}/public/visit/${visitId}`, {
      headers: { 'X-Guest-Session': sessionId },
    }),

  cancel: (visitId: string, sessionId: string, body: { reason?: string }) =>
    axios.post(`${base}/public/visit/${visitId}/cancel`, body, {
      headers: { 'X-Guest-Session': sessionId },
    }),

  reschedule: (
    visitId: string,
    sessionId: string,
    body: { startTime: string; professionalId?: string },
  ) =>
    axios.post(`${base}/public/visit/${visitId}/reschedule`, body, {
      headers: { 'X-Guest-Session': sessionId },
    }),

  approve: (visitId: string, sessionId: string) =>
    axios.post(
      `${base}/public/visit/${visitId}/approve`,
      {},
      {
        headers: { 'X-Guest-Session': sessionId },
      },
    ),

  reject: (visitId: string, sessionId: string, body: { reason?: string }) =>
    axios.post(`${base}/public/visit/${visitId}/reject`, body, {
      headers: { 'X-Guest-Session': sessionId },
    }),

  availableSlots: (visitId: string, sessionId: string) =>
    axios.get(`${base}/public/visit/${visitId}/available-slots`, {
      headers: { 'X-Guest-Session': sessionId },
    }),

  /**
   * Retry a FAILED guest payment via the session-gated mirror of the
   * URL-token-gated `/visit/public/guest-payment/:visitId/retry`. The visit
   * page uses this when the FE has a guest session (magic-link landing); the
   * legacy URL-token retry is still used by the booking-completed page where
   * the token is in scope.
   *
   * Returns `{ paymentUrl }` — the new P24/Autopay checkout URL the FE
   * redirects to (`window.location.assign(...)`).
   */
  paymentRetry: (visitId: string, sessionId: string) =>
    axios.post<{ paymentUrl: string }>(
      `${base}/public/visit/${visitId}/payment/retry`,
      {},
      {
        headers: { 'X-Guest-Session': sessionId },
      },
    ),

  /**
   * Patch the guest visit's embedded pet profileData (no Profile record exists
   * for guest visits). Body keys are all optional — only set fields are sent.
   */
  updateProfileData: (
    visitId: string,
    sessionId: string,
    body: {
      name?: string;
      breed?: string;
      petCategory?: 'dog' | 'cat' | 'other';
      weight?: number;
      gender?: string;
      dob?: string;
      chipNumber?: string;
    },
  ) =>
    axios.patch(`${base}/public/visit/${visitId}/profile-data`, body, {
      headers: { 'X-Guest-Session': sessionId },
    }),

  /**
   * Patch the guest visit's embedded owner contact (firstName/lastName/email/
   * phoneNumber). Read-only fields on `userData` such as marketing/terms
   * agreements are preserved server-side. Body keys are all optional — only
   * changed fields are sent.
   */
  updateUserData: (
    visitId: string,
    sessionId: string,
    body: {
      firstName?: string;
      lastName?: string;
      email?: string;
      phoneNumber?: string;
    },
  ) =>
    axios.patch(`${base}/public/visit/${visitId}/user-data`, body, {
      headers: { 'X-Guest-Session': sessionId },
    }),

  /**
   * Patch the guest visit's description ("Dolegliwości") and attached
   * documents. Diff-based — `documentsToAdd` rows insert into the documents
   * collection, `documentIdsToRemove` deletes them (server scopes the
   * delete to this visit). Body keys are all optional.
   */
  updateDetails: (
    visitId: string,
    sessionId: string,
    body: GuestUpdateVisitDetailsDto,
  ) =>
    axios.patch(`${base}/public/visit/${visitId}/details`, body, {
      headers: { 'X-Guest-Session': sessionId },
    }),

  /**
   * Edit a reminder attached to this visit. Recurrence is pinned to
   * SINGLE server-side — the guest can only change description /
   * scheduledAt / notificationChannels.
   */
  updateReminder: (
    visitId: string,
    sessionId: string,
    reminderId: string,
    body: {
      description?: string;
      scheduledAt?: string;
      notificationChannels?: ('EMAIL' | 'SMS')[];
    },
  ) =>
    axios.patch(
      `${base}/public/visit/${visitId}/reminders/${reminderId}`,
      body,
      {
        headers: { 'X-Guest-Session': sessionId },
      },
    ),

  /**
   * Delete a reminder attached to this visit. 404 if the reminder is
   * not bound to this visit.
   */
  deleteReminder: (visitId: string, sessionId: string, reminderId: string) =>
    axios.delete(`${base}/public/visit/${visitId}/reminders/${reminderId}`, {
      headers: { 'X-Guest-Session': sessionId },
    }),

  /**
   * Get a Twilio video access token for the guest to join the live
   * visit room. Returns 404 if the professional hasn't started yet.
   */
  videoToken: (visitId: string, sessionId: string) =>
    axios.get<{ token: string; roomName: string }>(
      `${base}/public/visit/${visitId}/video-token`,
      { headers: { 'X-Guest-Session': sessionId } },
    ),

  /**
   * Guest-session upload of a private file attached to the visit. Returns the
   * storage filename + a short-lived (60 min) signed URL for immediate preview;
   * the canonical Document row is created later by the updateDetails claim.
   * Uses raw axios + the X-Guest-Session header because the shared axios
   * client doesn't carry guest-session credentials.
   */
  uploadVisitPrivate: (visitId: string, sessionId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post<VisitGuestAttachmentControllerUploadVisitPrivate201Response>(
      `${base}/public/visit/${visitId}/upload-private`,
      formData,
      {
        headers: {
          'X-Guest-Session': sessionId,
          'Content-Type': 'multipart/form-data',
        },
      },
    );
  },

  /**
   * Submit a rating for the completed guest visit. Returns 409 if a
   * rating already exists — the FE refetches the visit on success and
   * surfaces the rating in read-only mode from `visit.rating`.
   */
  rate: (
    visitId: string,
    sessionId: string,
    body: {
      professionalRating: number;
      organizationRating: number;
      review?: string;
    },
  ) =>
    axios.post(`${base}/public/visit/${visitId}/rating`, body, {
      headers: { 'X-Guest-Session': sessionId },
    }),
};
